// Definição compartilhada dos relatórios (hub, atalhos do menu, rotas e páginas
// de visualização). Apenas dados — ícones ficam nos componentes que renderizam.
//
// Flags por relatório:
//   temDatas      → filtros dataInicio/dataFim
//   temStatus     → filtro status; use statusOpcoes para trocar a lista padrão
//   temPaciente   → exige selecionar um paciente (vai como idPaciente)
//   temDataReferencia → uma data só, em vez de intervalo; o campo enviado é
//                   dataFim por padrão, ou o nome em campoDataReferencia
//   somenteAdmin  → só aparece no catálogo para ROLE_ADMIN (a rota do back-end
//                   também é hasRole("ADMIN"))
//   categoria     → filtro do catálogo; os valores válidos são CATEGORIAS

// Valores do enum StatusFinanceiro do back-end — antes a lista trazia
// "A_VENCER", que não existe no enum e era recusado na conversão.
export const STATUS_FINANCEIRO = [
    { value: '', label: 'Todos' },
    { value: 'PENDENTE', label: 'Pendente' },
    { value: 'PARCIAL', label: 'Parcial' },
    { value: 'PAGO', label: 'Pago' },
    { value: 'VENCIDO', label: 'Vencido' },
    { value: 'CANCELADO', label: 'Cancelado' },
];

export const STATUS_TRATAMENTO = [
    { value: '', label: 'Todos' },
    { value: 'ATIVO', label: 'Ativo' },
    { value: 'PAUSADO', label: 'Pausado' },
    { value: 'ALTA', label: 'Alta' },
    { value: 'EVADIDO', label: 'Evadido' },
];

export const STATUS_SESSAO = [
    { value: '', label: 'Todos' },
    { value: 'AGENDADA', label: 'Agendada' },
    { value: 'REALIZADA', label: 'Realizada' },
    { value: 'REALIZADA_ALTERADA', label: 'Realizada (alterada)' },
    { value: 'FALTA', label: 'Falta' },
    { value: 'CANCELADA', label: 'Cancelada' },
];

export const NIVEL_RISCO = [
    { value: '', label: 'Todos' },
    { value: 'BAIXO', label: 'Baixo' },
    { value: 'MODERADO', label: 'Moderado' },
    { value: 'ALTO', label: 'Alto' },
    { value: 'CRITICO', label: 'Crítico' },
];

// Valores do enum Usuario_role do back-end. O relatório de usuários usa o
// filtro de status para o perfil — é o único campo de recorte que ele tem.
export const PERFIL_USUARIO = [
    { value: '', label: 'Todos' },
    { value: 'ROLE_ADMIN', label: 'Administrador' },
    { value: 'ROLE_USUARIO', label: 'Usuário' },
    { value: 'ROLE_VISUALIZADOR', label: 'Visualizador' },
];

export const STATUS_RENOVACAO = [
    { value: '', label: 'Todas' },
    { value: 'PENDENTE', label: 'Pendente' },
    { value: 'ACEITA', label: 'Aceita' },
    { value: 'RECUSADA', label: 'Recusada' },
    { value: 'EXPIRADA', label: 'Expirada' },
];

// Valores dos enums StatusLote e StatusGuia do back-end (módulo convênio).
export const STATUS_LOTE = [
    { value: '', label: 'Todos' },
    { value: 'ABERTO', label: 'Aberto' },
    { value: 'ENVIADO', label: 'Enviado' },
    { value: 'CONCILIADO', label: 'Conciliado' },
    { value: 'CANCELADO', label: 'Cancelado' },
];

export const STATUS_GUIA = [
    { value: '', label: 'Todas' },
    { value: 'EMITIDA', label: 'Emitida' },
    { value: 'FATURADA', label: 'Faturada' },
    { value: 'PAGA', label: 'Paga' },
    { value: 'GLOSADA', label: 'Glosada' },
    { value: 'CANCELADA', label: 'Cancelada' },
];

// Valores do enum TipoDocumento do back-end.
export const TIPO_DOCUMENTO = [
    { value: '', label: 'Todos os tipos' },
    { value: 'ATESTADO_PSICOLOGICO', label: 'Atestado psicológico' },
    { value: 'DECLARACAO', label: 'Declaração' },
    { value: 'RELATORIO_PSICOLOGICO', label: 'Relatório psicológico' },
    { value: 'LAUDO_PSICOLOGICO', label: 'Laudo psicológico' },
    { value: 'PARECER_PSICOLOGICO', label: 'Parecer psicológico' },
    { value: 'ENCAMINHAMENTO', label: 'Encaminhamento' },
    { value: 'TERMO_CONSENTIMENTO', label: 'Termo de consentimento' },
    { value: 'CONTRATO_SERVICO', label: 'Contrato de serviço' },
    { value: 'RECEITUARIO', label: 'Receituário' },
    { value: 'PEDIDO_MEDICO', label: 'Pedido médico' },
    { value: 'RECIBO', label: 'Recibo' },
];

export const RELATORIOS = [
    // ── Cadastros ────────────────────────────────────────────────────────────
    {
        key: 'psicologos',
        titulo: 'Psicólogos',
        descricao: 'Lista de psicólogos cadastrados com CRP e contatos.',
        categoria: 'Cadastros',
    },
    {
        key: 'pacientes',
        titulo: 'Pacientes',
        descricao: 'Pacientes por período de cadastro e status do tratamento, com contatos.',
        temDatas: true,
        temStatus: true,
        statusOpcoes: STATUS_TRATAMENTO,
        rotuloStatus: 'Status do tratamento',
        categoria: 'Cadastros',
    },
    {
        key: 'usuarios',
        titulo: 'Usuários do Sistema',
        descricao: 'Contas de acesso por perfil, com situação, último login e troca de senha.',
        temDatas: true,
        temStatus: true,
        statusOpcoes: PERFIL_USUARIO,
        rotuloStatus: 'Perfil',
        // GET /api/relatorios/usuarios/** é hasRole("ADMIN") em
        // SecurityConfigurations: entrega a mesma lista do GET /api/usuarios.
        // Sem isso o card abriria uma página que só sabe responder 403.
        somenteAdmin: true,
        categoria: 'Cadastros',
    },

    // ── Atendimento e agenda ─────────────────────────────────────────────────
    {
        key: 'sessoes-periodo',
        titulo: 'Sessões por Período',
        descricao: 'Sessões do intervalo com paciente, psicólogo, modalidade e status.',
        temDatas: true,
        temStatus: true,
        statusOpcoes: STATUS_SESSAO,
        categoria: 'Atendimentos',
    },
    {
        key: 'sessoes-paciente',
        titulo: 'Sessões por Paciente',
        descricao: 'Histórico de atendimentos de um paciente, com realizadas, canceladas e faltas.',
        temDatas: true,
        temPaciente: true,
        categoria: 'Atendimentos',
    },
    {
        key: 'sessoes-psicologo',
        titulo: 'Produtividade por Psicólogo',
        descricao: 'Quantidade de atendimentos por profissional e aproveitamento no período.',
        temDatas: true,
        categoria: 'Atendimentos',
    },
    {
        key: 'agenda-ocupacao',
        titulo: 'Agenda e Ocupação',
        descricao: 'Horários ocupados e livres por psicólogo, com taxa de ocupação.',
        temDatas: true,
        categoria: 'Atendimentos',
    },
    {
        key: 'fila-espera',
        titulo: 'Fila de Espera',
        descricao: 'Pacientes em fila com prioridade, data de entrada e tempo de espera.',
        categoria: 'Atendimentos',
    },
    {
        key: 'faltas-cancelamentos',
        titulo: 'Faltas e Cancelamentos',
        descricao: 'Sessões canceladas ou com falta no período, para acompanhar evasão.',
        temDatas: true,
        categoria: 'Atendimentos',
    },
    {
        key: 'avaliacao-risco',
        titulo: 'Avaliação de Risco',
        descricao: 'Avaliações registradas por nível de risco, com indicadores e conduta adotada.',
        temDatas: true,
        temStatus: true,
        statusOpcoes: NIVEL_RISCO,
        rotuloStatus: 'Nível de risco',
        categoria: 'Atendimentos',
    },

    // ── Clínico ──────────────────────────────────────────────────────────────
    {
        key: 'anamneses',
        titulo: 'Anamneses',
        descricao: 'Anamneses preenchidas no período, com autor, versão e última alteração.',
        temDatas: true,
        categoria: 'Clínico',
    },
    {
        key: 'prontuarios-cid',
        titulo: 'Prontuários por CID',
        descricao: 'Distribuição dos prontuários por diagnóstico (CID), com percentual sobre o total.',
        temDatas: true,
        categoria: 'Clínico',
    },

    // ── Documentos ───────────────────────────────────────────────────────────
    {
        key: 'documentos-emitidos',
        titulo: 'Documentos Emitidos',
        descricao: 'Atestados, declarações, laudos e demais documentos emitidos no período, com situação da assinatura.',
        temDatas: true,
        temStatus: true,
        statusOpcoes: TIPO_DOCUMENTO,
        rotuloStatus: 'Tipo de documento',
        categoria: 'Clínico',
    },

    // ── Assinaturas ──────────────────────────────────────────────────────────
    {
        key: 'assinaturas-ativas',
        titulo: 'Assinaturas Ativas',
        descricao: 'Pacientes com plano ativo, periodicidade, valor e sessões restantes.',
        temDatas: true,
        categoria: 'Assinaturas',
    },
    {
        key: 'renovacoes-periodo',
        titulo: 'Renovações por Período',
        descricao: 'Renovações solicitadas no intervalo, por situação, com taxa de aceite.',
        temDatas: true,
        temStatus: true,
        statusOpcoes: STATUS_RENOVACAO,
        rotuloStatus: 'Situação da renovação',
        categoria: 'Assinaturas',
    },

    // ── Convênio ─────────────────────────────────────────────────────────────
    {
        key: 'guias-lote',
        titulo: 'Guias por Lote',
        descricao: 'Lotes de faturamento por competência, com guias, valor, repasse e glosa.',
        temDatas: true,
        temStatus: true,
        statusOpcoes: STATUS_LOTE,
        rotuloStatus: 'Situação do lote',
        categoria: 'Convênio',
    },
    {
        key: 'repasse-plano-saude',
        titulo: 'Repasse por Plano de Saúde',
        descricao: 'Produção, apresentado, repasse recebido, glosa e a receber por operadora.',
        temDatas: true,
        temStatus: true,
        statusOpcoes: STATUS_GUIA,
        rotuloStatus: 'Situação da guia',
        categoria: 'Convênio',
    },

    // ── Financeiro ───────────────────────────────────────────────────────────
    {
        key: 'contas-pagar',
        titulo: 'Contas a Pagar',
        descricao: 'Títulos a pagar por vencimento e status, com pago e em aberto.',
        temDatas: true,
        temStatus: true,
        statusOpcoes: STATUS_FINANCEIRO,
        categoria: 'Financeiro',
    },
    {
        key: 'despesas-categoria',
        titulo: 'Despesas por Categoria',
        descricao: 'Total por categoria de despesa, com percentual sobre o total do período.',
        temDatas: true,
        temStatus: true,
        statusOpcoes: STATUS_FINANCEIRO,
        categoria: 'Financeiro',
    },
    {
        key: 'despesas-recorrentes',
        titulo: 'Despesas Recorrentes',
        descricao: 'Despesas marcadas como recorrentes, com dia de recorrência e valor previsto.',
        categoria: 'Financeiro',
    },
    {
        key: 'pagamentos-periodo',
        titulo: 'Pagamentos por Período',
        descricao: 'Despesas efetivamente pagas no intervalo, pela data de pagamento.',
        temDatas: true,
        categoria: 'Financeiro',
    },
    {
        key: 'despesas-psicologo',
        titulo: 'Despesas por Psicólogo',
        descricao: 'Gastos vinculados a cada profissional, com pago e em aberto.',
        temDatas: true,
        categoria: 'Financeiro',
    },
    {
        key: 'recebimentos-periodo',
        titulo: 'Recebimentos por Período',
        descricao: 'O que entrou no intervalo, pela data da baixa, com forma e conta.',
        temDatas: true,
        categoria: 'Financeiro',
    },
    {
        key: 'inadimplencia',
        titulo: 'Inadimplência',
        descricao: 'Títulos vencidos e não liquidados, com dias de atraso e acumulado por paciente.',
        // Data única: a pergunta é "vencido até quando", não um intervalo.
        temDataReferencia: true,
        rotuloDataReferencia: 'Vencidos até',
        categoria: 'Financeiro',
    },
    {
        key: 'receita-paciente',
        titulo: 'Receita por Paciente',
        descricao: 'Total recebido de cada paciente no período, com ticket médio.',
        temDatas: true,
        categoria: 'Financeiro',
    },
    {
        key: 'previsao-recebimentos',
        titulo: 'Previsão de Recebimentos',
        descricao: 'Títulos a vencer agrupados por mês, projetando a entrada futura.',
        temDataReferencia: true,
        rotuloDataReferencia: 'A vencer a partir de',
        campoDataReferencia: 'dataInicio',
        categoria: 'Financeiro',
    },
    {
        key: 'recibos-emitidos',
        titulo: 'Recibos Emitidos',
        descricao: 'Relação dos recibos com número e valor, para conferência contábil.',
        temDatas: true,
        categoria: 'Financeiro',
    },
    {
        key: 'contas-receber',
        titulo: 'Contas a Receber',
        descricao: 'Lançamentos por vencimento e status, com totais recebido e em aberto.',
        temDatas: true,
        temStatus: true,
        statusOpcoes: STATUS_FINANCEIRO,
        categoria: 'Financeiro',
    },

    // ── Contas bancárias e planos de pagamento ──────────────────────────────
    {
        key: 'contas-bancarias',
        titulo: 'Contas Bancárias',
        descricao: 'Cadastro das contas com banco, agência, PIX, tipo e situação.',
        categoria: 'Financeiro',
    },
    {
        key: 'saldos-conta',
        titulo: 'Saldos por Conta',
        descricao: 'Saldo inicial, atual e variação, com entradas e saídas do período.',
        temDatas: true,
        categoria: 'Financeiro',
    },
    {
        key: 'contas-psicologo',
        titulo: 'Contas por Psicólogo',
        descricao: 'Contas bancárias vinculadas a cada profissional, com saldo somado.',
        categoria: 'Financeiro',
    },
    {
        key: 'movimentacao-conta',
        titulo: 'Movimentação por Conta',
        descricao: 'Entradas, saídas e líquido de cada conta no período.',
        temDatas: true,
        categoria: 'Financeiro',
    },
    {
        key: 'planos-pagamento',
        titulo: 'Planos de Pagamento',
        descricao: 'Cadastro dos planos com formas aceitas, conta vinculada e situação.',
        categoria: 'Financeiro',
    },
    {
        key: 'faturamento-plano',
        titulo: 'Faturamento por Plano',
        descricao: 'Total recebido em cada plano de pagamento no período.',
        temDatas: true,
        categoria: 'Financeiro',
    },
    {
        key: 'uso-planos',
        titulo: 'Uso de Planos',
        descricao: 'Quantos títulos a receber e a pagar estão vinculados a cada plano.',
        categoria: 'Financeiro',
    },
    // ── Gerenciais ──────────────────────────────────────────────────
    {
        key: 'fluxo-caixa',
        titulo: 'Fluxo de Caixa',
        descricao: 'Entradas, saídas e resultado de cada mês, com saldo acumulado do período.',
        temDatas: true,
        categoria: 'Financeiro',
    },
    {
        key: 'resultado-dre',
        titulo: 'Resultado (DRE)',
        descricao: 'Receita recebida menos despesa paga, com as despesas abertas por categoria.',
        temDatas: true,
        categoria: 'Financeiro',
    },
    {
        key: 'faturamento-mensal',
        titulo: 'Faturamento Mensal',
        descricao: 'Evolução mês a mês da receita, com a variação sobre o mês anterior.',
        temDatas: true,
        categoria: 'Financeiro',
    },
    {
        key: 'receitas-forma-pagamento',
        titulo: 'Receitas por Forma de Pagamento',
        descricao: 'Quanto entrou em PIX, dinheiro, cartões, boleto e transferência.',
        temDatas: true,
        categoria: 'Financeiro',
    },
    {
        key: 'ticket-medio',
        titulo: 'Ticket Médio',
        descricao: 'Receita do mês por sessão atendida e por paciente pagante.',
        temDatas: true,
        categoria: 'Financeiro',
    },
];

/** Filtros do catálogo, na ordem em que aparecem. "Todos" é a aba inicial. */
export const CATEGORIAS = [
    'Todos', 'Cadastros', 'Atendimentos', 'Clínico', 'Assinaturas', 'Convênio',
    'Financeiro',
];

// Os rótulos e a ordem dos indicadores saíram daqui: vêm do back-end, em
// `indicadores` na resposta de /dados, calculados por RotulosTotais. Eram a
// mesma tabela mantida em dois lugares (aqui e dentro de cada template .jrxml),
// e o cartão da tela podia divergir do cartão do PDF.

export const getRelatorio = (key) => RELATORIOS.find(r => r.key === key);

export const getStatusOpcoes = (cfg) => cfg?.statusOpcoes ?? STATUS_FINANCEIRO;

/**
 * Texto comparavel: sem acento e sem caixa.
 *
 * É o que faz "sessoes" achar "Sessões" e "psicologo" achar "Psicólogo" — num
 * sistema em português, exigir o acento na busca é exigir que o usuário saiba
 * como o rótulo foi escrito.
 */
export const normalizarTexto = (s) => (s ?? '')
    .toString()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

/** O relatório casa com o termo pelo título ou pela descrição. */
export const casaComBusca = (relatorio, termo) => {
    const t = normalizarTexto(termo).trim();
    if (!t) return true;
    return normalizarTexto(relatorio.titulo).includes(t)
        || normalizarTexto(relatorio.descricao).includes(t);
};

/**
 * Catálogo visível para o perfil: `somenteAdmin` sai da lista de quem não é
 * ADMIN, para o card não abrir uma página que o back-end só sabe responder 403.
 */
export const relatoriosVisiveis = (admin = true) =>
    admin ? RELATORIOS : RELATORIOS.filter(r => !r.somenteAdmin);

/** Relatórios visíveis que casam com a busca e a categoria. */
export const filtrarRelatorios = (termo, categoria = 'Todos', admin = true) =>
    relatoriosVisiveis(admin).filter(r =>
        (categoria === 'Todos' || r.categoria === categoria) && casaComBusca(r, termo));

/**
 * Quantos relatórios cada aba tem para o termo buscado.
 *
 * A contagem respeita a busca de propósito: aba mostrando "7" e abrindo vazia
 * porque o número ignorava o termo é pior que número nenhum.
 */
export const contarPorCategoria = (termo, admin = true) => {
    const base = relatoriosVisiveis(admin).filter(r => casaComBusca(r, termo));
    const out = { Todos: base.length };
    CATEGORIAS.filter(c => c !== 'Todos')
        .forEach(c => { out[c] = base.filter(r => r.categoria === c).length; });
    return out;
};
